import { Spin } from "antd";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { Typography } from "../components";
import { config } from "../config";
import { sentryCapture, useApiContext } from "../utils";

import { AUTH_REDIRECT_URL_KEY } from "./AuthGuard";

const Layout = styled.div`
  height: 100vh;
  text-align: center;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const SLACK_OAUTH_SCOPES =
  "channels:read chat:write team:read users:read groups:read im:read im:write mpim:write channels:join links:write links:read";

const buildSlackAuthUrl = (oauthAttemptId: string, email: string): string => {
  const redirectUri = `${config.OAUTH_REDIRECT_URL_BASE}/slack`;

  const state = JSON.stringify({
    from: "webapp",
    oauth_attempt_id: oauthAttemptId,
    email: email,
    data_source_type: "slack",
  });

  const params = new URLSearchParams({
    client_id: config.SLACK_CLIENT_ID,
    redirect_uri: redirectUri,
    scope: SLACK_OAUTH_SCOPES,
    prompt: "consent",
    response_type: "code",
    state: state,
  });

  return `https://slack.com/oauth/v2/authorize?${params.toString()}`;
};

export const SlackMarketplaceInstall: React.FC = () => {
  const { apiClient } = useApiContext();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    initiateSlackOAuth();
  }, []);

  const initiateSlackOAuth = async () => {
    try {
      setIsLoading(true);

      // Clear the redirect URL from sessionStorage to prevent redirect loop after OAuth
      sessionStorage.removeItem(AUTH_REDIRECT_URL_KEY);

      // Call the marketplace-specific endpoint to initiate OAuth
      const response = await apiClient.initiateSlackMarketplaceOauth();
      const { oauth_attempt_id, user_email, slack_auth_url } = response.data;

      // If backend returned a pre-built URL, use it; otherwise build it ourselves
      const authUrl = slack_auth_url || buildSlackAuthUrl(oauth_attempt_id, user_email);

      // Redirect to Slack OAuth - use window.location.href for full page redirect
      window.location.href = authUrl;
    } catch (err) {
      sentryCapture({ error: err as Error });
      setError("Failed to initiate Slack authorization. Please try again.");
      setIsLoading(false);
    }
  };

  if (error) {
    return (
      <Layout>
        <Typography variant="body1" fontWeight={600}>
          Error
        </Typography>
        <Typography>{error}</Typography>
      </Layout>
    );
  }

  return (
    <Layout>
      <Spin size="large" />
      <Typography variant="body1" fontWeight={600}>
        Redirecting to Slack...
      </Typography>
      <Typography>Please wait while we connect you to Slack for authorization.</Typography>
    </Layout>
  );
};
